import React, { useCallback, useEffect, useState } from 'react';
import { Form, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { CENTRAL_PHARMACY_PATHS } from '../pharmacyPaths';
import { PHARMACY_COPY, statusLabel } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock, EmptyBlock, formatDate, formatNumber } from '../ui';
import {
  OrderFlow,
  nextActionLabel,
  orderStatusClass,
  seedReceptions
} from '../components/orderUi';

const copy = PHARMACY_COPY.ORDERS;

export default function PharmacyOrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { toast, centralSite } = usePharmacy();
  const [detail, setDetail] = useState(null);
  const [receptions, setReceptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pharmacyService.getOrder(orderId);
      if (res.success) {
        setDetail(res.data);
        setReceptions(seedReceptions(res.data));
      } else {
        setDetail(null);
        toast('danger', res.message || 'Commande introuvable.');
      }
    } catch (e) {
      setDetail(null);
      toast('danger', e.message || 'Commande introuvable.');
    } finally {
      setLoading(false);
    }
  }, [orderId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const goBack = () => navigate(CENTRAL_PHARMACY_PATHS.ORDERS);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res.success) {
        toast('success', okMsg);
        if (res.data) {
          setDetail(res.data);
          setReceptions(seedReceptions(res.data));
        } else {
          await load();
        }
      } else {
        toast('danger', res.message || 'Échec.');
      }
    } catch (e) {
      toast('danger', e.message || 'Échec.');
    } finally {
      setBusy(false);
    }
  };

  const patchReception = (itemId, patch) => {
    setReceptions((p) => ({ ...p, [itemId]: { ...p[itemId], ...patch } }));
  };

  const submitReceive = async () => {
    const payload = {
      receptions: Object.entries(receptions)
        .map(([item_id, r]) => ({
          item_id: parseInt(item_id, 10),
          quantity_received: parseInt(r.quantity_received, 10) || 0,
          lot_number: (r.lot_number || '').trim(),
          expiry_date: r.expiry_date || null
        }))
        .filter((r) => r.quantity_received > 0)
    };
    if (!payload.receptions.length) {
      toast('warning', copy.RECEIVE_WARN);
      return;
    }
    const missingLot = payload.receptions.some((r) => !r.lot_number || !r.expiry_date);
    if (missingLot) {
      toast('warning', copy.RECEIVE_LOT_REQUIRED);
      return;
    }
    await act(() => pharmacyService.receiveOrder(detail.id, payload), copy.RECEIVE_OK);
  };

  const cancelOrder = async () => {
    if (!window.confirm(copy.CONFIRM_CANCEL.replace('{number}', detail.order_number))) return;
    await act(() => pharmacyService.cancelOrder(detail.id), 'Commande annulée.');
  };

  const canReceive = detail && ['VALIDATED', 'PENDING'].includes(detail.status);

  if (loading) return <LoadingBlock label="Chargement de la commande…" />;
  if (!detail) {
    return (
      <div className="cp-order-page">
        <EmptyBlock label="Commande introuvable." />
        <button type="button" className="cp-btn cp-btn--ghost mt-3" onClick={goBack}>
          {copy.BACK_TO_LIST}
        </button>
      </div>
    );
  }

  return (
    <div className="cp-order-page">
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={detail.order_number}
        description={
          <span className="d-inline-flex flex-wrap align-items-center gap-2">
            <span className={`cp-status-pill ${orderStatusClass(detail.status)}`}>
              {statusLabel(detail.status)}
            </span>
            <span className="text-muted">{nextActionLabel(detail.status)}</span>
          </span>
        }
        actions={
          <button type="button" className="cp-btn cp-btn--ghost" onClick={goBack}>
            <Icon name="chevronLeft" size={16} aria-hidden />
            {copy.BACK_TO_LIST}
          </button>
        }
      />

      <OrderFlow status={detail.status} />

      <div className="cp-order-meta">
        <div>
          <span className="cp-order-meta__label">{copy.DETAIL_SUPPLIER}</span>
          <strong>{detail.supplier_name || '—'}</strong>
        </div>
        <div>
          <span className="cp-order-meta__label">{copy.DETAIL_SITE}</span>
          <strong>{detail.site_name || centralSite?.name || '—'}</strong>
        </div>
        <div>
          <span className="cp-order-meta__label">{copy.DETAIL_EXPECTED}</span>
          <strong>{detail.expected_date ? formatDate(detail.expected_date) : '—'}</strong>
        </div>
      </div>

      {detail.notes ? (
        <div className="cp-order-notes">
          <span className="cp-order-meta__label">{copy.DETAIL_NOTES}</span>
          <p className="mb-0">{detail.notes}</p>
        </div>
      ) : null}

      <section className="cp-form-section">
        <h3 className="cp-form-section__title">{copy.SECTION_ITEMS}</h3>
        <div className="cp-table-wrap">
          <Table responsive className="cp-table mb-0">
            <thead>
              <tr>
                <th>{copy.COL_MED}</th>
                <th className="text-end">{copy.COL_ORDERED}</th>
                <th className="text-end">{copy.COL_RECEIVED}</th>
                <th className="text-end">{copy.COL_REMAINING}</th>
              </tr>
            </thead>
            <tbody>
              {(detail.items || []).map((it) => {
                const remaining = Math.max(
                  0,
                  Number(it.quantity_ordered) - Number(it.quantity_received || 0)
                );
                return (
                  <tr key={it.id}>
                    <td>
                      <strong>{it.medication_name}</strong>
                    </td>
                    <td className="text-end cp-num">{formatNumber(it.quantity_ordered)}</td>
                    <td className="text-end cp-num">{formatNumber(it.quantity_received)}</td>
                    <td className="text-end cp-num">
                      <span className={remaining > 0 ? 'cp-remain--open' : 'cp-remain--done'}>
                        {formatNumber(remaining)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </section>

      {canReceive ? (
        <section className="cp-form-section cp-form-section--receive">
          <h3 className="cp-form-section__title">{copy.SECTION_RECEIVE}</h3>
          <p className="cp-receive-hint">{copy.RECEIVE_HINT}</p>
          <div className="cp-receive-list">
            {(detail.items || []).map((it) => {
              const remaining = Math.max(
                0,
                Number(it.quantity_ordered) - Number(it.quantity_received || 0)
              );
              if (remaining <= 0) return null;
              const rec = receptions[it.id] || {};
              return (
                <article key={it.id} className="cp-receive-card">
                  <header className="cp-receive-card__head">
                    <strong>{it.medication_name}</strong>
                    <span className="cp-receive-card__remain">Reste {formatNumber(remaining)}</span>
                  </header>
                  <div className="cp-receive-card__grid">
                    <Form.Group className="cp-form-field">
                      <Form.Label>{copy.COL_TO_RECEIVE} *</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        max={remaining}
                        size="sm"
                        value={rec.quantity_received ?? ''}
                        onChange={(e) => patchReception(it.id, { quantity_received: e.target.value })}
                      />
                    </Form.Group>
                    <Form.Group className="cp-form-field">
                      <Form.Label>{copy.COL_LOT} *</Form.Label>
                      <Form.Control
                        size="sm"
                        value={rec.lot_number ?? ''}
                        onChange={(e) => patchReception(it.id, { lot_number: e.target.value })}
                        placeholder="LOT-…"
                      />
                    </Form.Group>
                    <Form.Group className="cp-form-field">
                      <Form.Label>{copy.COL_EXPIRY} *</Form.Label>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={rec.expiry_date ?? ''}
                        onChange={(e) => patchReception(it.id, { expiry_date: e.target.value })}
                      />
                    </Form.Group>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      <div className="cp-order-page__actions">
        <div className="cp-order-page__actions-btns ms-auto">
          {detail.status === 'DRAFT' ? (
            <button
              type="button"
              className="cp-btn cp-btn--ghost"
              disabled={busy}
              onClick={() => act(() => pharmacyService.submitOrder(detail.id), 'Commande soumise.')}
            >
              {copy.ACT_SUBMIT}
            </button>
          ) : null}
          {['PENDING', 'DRAFT'].includes(detail.status) ? (
            <button
              type="button"
              className="cp-btn cp-btn--primary"
              disabled={busy}
              onClick={() => act(() => pharmacyService.validateOrder(detail.id), 'Commande validée.')}
            >
              {copy.ACT_VALIDATE}
            </button>
          ) : null}
          {canReceive ? (
            <button type="button" className="cp-btn cp-btn--primary" disabled={busy} onClick={submitReceive}>
              {copy.ACT_RECEIVE}
            </button>
          ) : null}
          {detail.status !== 'RECEIVED' && detail.status !== 'CANCELLED' ? (
            <button type="button" className="cp-btn cp-btn--danger" disabled={busy} onClick={cancelOrder}>
              {copy.ACT_CANCEL_ORDER}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
